import { useEffect } from "react";

/**
 * FAZ-93 madde 4 — KAYDIRMAYA DUYARLI NAVBAR.
 *
 * Kullanıcı: "Tüm sayfalarda yukarı kaydırınca navbar kaybolsun, sayfa
 * aşağı inince gelsin. Ama sekmeli yapılar hep orada."
 *
 * NEDEN DOM BAYRAĞI, NEDEN setState DEĞİL
 * ---------------------------------------
 * Bilgiyi üç ayrı ağaç okumalı: navbar (`BelediyeNavbar`), kaydıran kap
 * (`BelediyeLayout`) ve yapışkan çip şeridi (`PageHeader`). React durumu
 * olsaydı her kaydırma karesinde üçü birden yeniden çizilirdi. Sistemin
 * deseni zaten bu: `data-msj-dock`, `data-gk-detay`, `data-msj-ekran`,
 * `data-kaydi` (FAZ-92).
 *
 * <html> üzerine iki bayrak yazılır:
 *   data-navbar="acik" | "gizli"   → navbar yukarı kayar
 *   data-kaydi="0" | "1"           → sayfa tepede değil (şerit gölgesi)
 *
 * `data-kaydi` ayrı bir bayrak, çünkü şeridin gölgesi navbarın gizli
 * olmasına değil sayfanın KAYDIRILMIŞ olmasına bağlı: kullanıcı aşağıda
 * durup yukarı doğru bir tık çekince navbar gelir ama sayfa hâlâ kaydırılmış
 * durumdadır ve şeridin altındaki içeriğin kestiği yerde "kat" görünmelidir.
 *
 * @param {React.RefObject<HTMLElement>} kapRef  overflow-y:auto olan kap
 * @param {boolean} etkin  false ise bayraklar temizlenir (masaüstü, detay)
 */
export default function useNavbarGizle(kapRef, etkin) {
  useEffect(() => {
    const kok = typeof document !== "undefined" ? document.documentElement : null;
    if (!kok) return undefined;

    const temizle = () => {
      kok.removeAttribute("data-navbar");
      kok.removeAttribute("data-kaydi");
    };

    const kap = kapRef && kapRef.current;
    if (!etkin || !kap) {
      temizle();
      return undefined;
    }

    /* Hareketi azalt tercihi: navbar hiç gizlenmez. Kaybolup gelen bir
       çubuk vestibüler rahatsızlık yaratabilen türden bir hareket. */
    const azalt =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let sonY = kap.scrollTop;
    let bekleyen = false;
    /* Bayrak değişince içerik kabının yüksekliği 56px oynar (CSS). Sayfanın
       EN DİBİNDEYSEK tarayıcı scrollTop'u kırpar ve bu yapay bir "yukarı
       kaydırma" olayı doğurur — navbar hemen geri gelir, yükseklik küçülür,
       gözle görülür bir zıplama olur. Bayrak yazıldıktan sonra geçiş süresi
       boyunca yön kararı alınmaz; kırpma olayı sessizce yutulur. */
    let kilitTimer = null;
    const KILIT_MS = 260; // CSS geçişi 220ms + pay

    /* 6px eşik: parmak titremesi navbarı yanıp söndürmesin. Altındaki
       hareketler "yön" sayılmaz. */
    const ESIK = 6;
    /* Tepenin 8px'i her zaman "tepede" sayılır — lastik-bant (overscroll)
       geri yaylanırken navbar bir kare gizlenmesin. */
    const TEPE = 8;

    /* İLK yazım kilitlemez: mount anındaki "acik" bir DEĞİŞİM değil, başlangıç
       durumudur. Kilitleseydi kancanın ilk 260ms'si sağır kalırdı — sayfaya
       girip hemen kaydıran kullanıcıda navbar hiç gizlenmezdi. */
    let ilk = true;

    /** Bayrağı yazar ve geçiş bitene kadar yeni karar almayı kilitler. */
    const yaz = (deger) => {
      if (kok.getAttribute("data-navbar") === deger) return;
      kok.setAttribute("data-navbar", deger);
      if (ilk) {
        ilk = false;
        return;
      }
      if (kilitTimer) clearTimeout(kilitTimer);
      kilitTimer = setTimeout(() => {
        kilitTimer = null;
        sonY = kap.scrollTop; // kırpma olduysa yeni taban buradan
      }, KILIT_MS);
    };

    const olc = () => {
      bekleyen = false;
      const y = kap.scrollTop;
      kok.setAttribute("data-kaydi", y > TEPE ? "1" : "0");

      if (kilitTimer) return; // geçiş sürüyor, yön kararı alınmaz
      if (azalt || y <= TEPE) {
        yaz("acik");
        sonY = y;
        return;
      }
      const fark = y - sonY;
      if (Math.abs(fark) < ESIK) return; // yön kararı verecek kadar hareket yok
      yaz(fark > 0 ? "gizli" : "acik");
      sonY = y;
    };

    const dinle = () => {
      if (bekleyen) return;
      bekleyen = true;
      // Ölçüm çizim karesine bağlanır; olay başına DOM yazımı yapılmaz.
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(olc);
      } else {
        olc();
      }
    };

    olc(); // ilk durum
    kap.addEventListener("scroll", dinle, { passive: true });
    return () => {
      kap.removeEventListener("scroll", dinle);
      if (kilitTimer) clearTimeout(kilitTimer);
      temizle();
    };
  }, [kapRef, etkin]);
}
